import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';

const libraries = ['geometry', 'drawing', 'places', 'marker'];
const MAP_ID = process.env.REACT_APP_GMAP_ID || 'DEMO_MAP_ID';

const SearchBoxMap = ({
  bounds,
  center,
  position,
  zoom,

  onBoundsChanged,
  onDragEnd,
  onMapMounted,
  onPlaceSelected,
  onClick,
}) => {
  const intl = useIntl();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GMAP_KEY,
    libraries,
  });

  const [map, setMap] = React.useState(null);
  const markerRef = React.useRef(null);
  const autocompleteHostRef = React.useRef(null);
  const autocompleteRef = React.useRef(null);

  const onDragEndRef = React.useRef(onDragEnd);
  const onPlaceSelectedRef = React.useRef(onPlaceSelected);
  React.useEffect(() => {
    onDragEndRef.current = onDragEnd;
  }, [onDragEnd]);
  React.useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  const handleMapLoad = (m) => {
    setMap(m);
    onMapMounted(m);
  };

  React.useEffect(() => {
    if (!isLoaded || !map || !autocompleteHostRef.current) return undefined;
    const { PlaceAutocompleteElement } = window.google.maps.places || {};
    if (!PlaceAutocompleteElement) return undefined;
    let pa;
    try {
      pa = new PlaceAutocompleteElement();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        'PlaceAutocompleteElement unavailable (check Places API (New) + key config)',
        e,
      );
      return undefined;
    }
    pa.style.width = '250px';
    try {
      pa.placeholder = intl.formatMessage({ id: 'Search', defaultMessage: 'Search' });
    } catch (e) {
      // placeholder not supported on this version; ignore
    }
    autocompleteHostRef.current.appendChild(pa);
    autocompleteRef.current = pa;

    const handler = async (event) => {
      const prediction = event.placePrediction;
      if (!prediction) return;
      const place = prediction.toPlace();
      try {
        await place.fetchFields({ fields: ['location', 'viewport'] });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch place fields', e);
        return;
      }
      onPlaceSelectedRef.current(place);
    };
    pa.addEventListener('gmp-select', handler);

    return () => {
      pa.removeEventListener('gmp-select', handler);
      pa.remove();
      autocompleteRef.current = null;
    };
  }, [isLoaded, intl, map]);

  React.useEffect(() => {
    if (autocompleteRef.current && bounds) {
      autocompleteRef.current.locationBias = bounds;
    }
  }, [bounds]);

  React.useEffect(() => {
    if (!isLoaded || !map) return undefined;
    const { AdvancedMarkerElement } = window.google.maps.marker || {};
    if (!AdvancedMarkerElement) return undefined;

    if (!position || !Number.isFinite(position.lat) || !Number.isFinite(position.lng)) {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      return undefined;
    }

    if (!markerRef.current) {
      let m;
      try {
        m = new AdvancedMarkerElement({
          map,
          position,
          gmpDraggable: true,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('AdvancedMarkerElement unavailable (check Map ID + key config)', e);
        return undefined;
      }
      m.addListener('dragend', () => {
        const p = m.position;
        const lat = typeof p.lat === 'function' ? p.lat() : p.lat;
        const lng = typeof p.lng === 'function' ? p.lng() : p.lng;
        onDragEndRef.current({ lat, lng });
      });
      markerRef.current = m;
    } else {
      markerRef.current.position = position;
    }
    return undefined;
  }, [isLoaded, map, position]);

  React.useEffect(
    () => () => {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
    },
    [],
  );

  if (!isLoaded) return null;

  return (
    <GoogleMap
      onLoad={handleMapLoad}
      options={{
        mapId: MAP_ID,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
      }}
      defaultZoom={7}
      zoom={zoom}
      center={center}
      onBoundsChanged={onBoundsChanged}
      mapContainerStyle={{ minHeight: '50vh', position: 'relative' }}
      onClick={onClick}
    >
      <div
        ref={autocompleteHostRef}
        role="presentation"
        style={{
          position: 'absolute',
          right: '10px',
          top: '10px',
          zIndex: 5,
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </GoogleMap>
  );
};

SearchBoxMap.propTypes = {
  bounds: PropTypes.shape({}),
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  position: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }).isRequired,
  zoom: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  onBoundsChanged: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onMapMounted: PropTypes.func.isRequired,
  onPlaceSelected: PropTypes.func.isRequired,
};

export default SearchBoxMap;
